<template>
  <v-container fluid>
    <div class="d-flex align-start mb-4">
      <div>
        <h2 class="page-title mb-1">Membresías por vencer</h2>
        <p class="page-desc mb-0">
          Membresías vencidas o que vencen en los próximos 40 días, ordenadas por
          urgencia. Desde cada fila se abre el perfil del usuario para renovar.
        </p>
      </div>
      <v-spacer />
      <v-btn icon :loading="loadingData" @click="loadData" title="Recargar">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </div>

    <!-- Resumen por ventana, mismo patrón que Renovaciones B2B. Se calcula
         sobre los demás filtros pero no sobre la ventana elegida, así que
         las tarjetas no se vacían al seleccionar una. -->
    <v-row class="mb-2">
      <v-col
        v-for="w in summaryWindows"
        :key="w.key"
        cols="6"
        md="3"
      >
        <v-card
          outlined
          class="pa-3 text-center window-card"
          :class="{ 'window-card--active': windowFilter === w.key }"
          :style="{ borderColor: w.color }"
          @click="toggleWindow(w.key)"
        >
          <div class="window-label" :style="{ color: w.color }">{{ w.label }}</div>
          <div class="window-count">{{ w.count }}</div>
          <div class="window-caption">{{ w.caption }}</div>
        </v-card>
      </v-col>
    </v-row>

    <v-data-table
      :headers="headers"
      :items="filteredItems"
      :loading="loadingData"
      :items-per-page="50"
      :search="search"
      class="elevation-1"
      :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
      :no-data-text="emptyText"
    >
      <template v-slot:top>
        <v-row class="px-4 pt-4 pb-0 align-center" dense>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="search"
              label="Buscar (mascota, usuario, email)"
              prepend-inner-icon="mdi-magnify"
              dense outlined clearable hide-details
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="companyFilterPending"
              :items="companyFilterItems"
              label="Empresa / Contrato"
              multiple
              dense outlined clearable hide-details
            >
              <template v-slot:selection="{ item, index }">
                <span v-if="allCompaniesSelected">
                  <span v-if="index === 0" class="grey--text text-caption">Todas ({{ companyFilterPending.length }})</span>
                </span>
                <template v-else>
                  <v-chip v-if="index < 2" small>{{ item.text }}</v-chip>
                  <span v-else-if="index === 2" class="grey--text text-caption ml-1">
                    +{{ companyFilterPending.length - 2 }} más
                  </span>
                </template>
              </template>
            </v-select>
          </v-col>
          <v-col cols="12" md="2" class="d-flex align-center">
            <v-switch
              v-model="churnFilterPending"
              label="Incluir churned"
              inset dense hide-details class="mt-0"
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-btn
              color="primary"
              block
              :disabled="!hasPendingChanges"
              @click="applyFilters"
            >
              <v-icon left small>mdi-filter-check-outline</v-icon>
              Aplicar filtro
            </v-btn>
          </v-col>
        </v-row>
        <div class="d-flex flex-wrap align-center px-4 pb-2">
          <span class="text-caption grey--text mr-2">Tipo:</span>
          <v-chip-group v-model="typeFilterPending" column multiple>
            <v-chip
              v-for="opt in typeFilterItems"
              :key="opt.value"
              :value="opt.value"
              filter outlined small
              :color="typeChipColor(opt.value)"
            >
              {{ opt.text }} · {{ opt.count }}
            </v-chip>
          </v-chip-group>
          <v-spacer />
          <v-btn v-if="hasActiveFilters" small text color="primary" @click="clearFilters">
            Limpiar filtros
          </v-btn>
        </div>
      </template>

      <template v-slot:item.lineaNegocio="{ item }">
        <v-chip x-small label :color="typeColor(item)" dark>{{ item.lineaNegocio || '—' }}</v-chip>
      </template>

      <template v-slot:item.expirationDate="{ item }">
        {{ item.expirationDate | date }}
      </template>

      <template v-slot:item.daysUntilExpiry="{ item }">
        <v-chip
          v-if="hasDays(item)"
          small
          :color="windowColor(windowKey(item))"
          class="white--text font-weight-bold"
        >
          {{ daysLabel(item) }}
        </v-chip>
        <span v-else class="grey--text">—</span>
      </template>

      <template v-slot:item.userName="{ item }">
        <div class="font-weight-medium">{{ item.userName }}</div>
        <div class="row-sub">{{ item.email }}</div>
      </template>

      <template v-slot:item.contratoCodigo="{ item }">
        <span v-if="item.company && item.company.name">
          {{ item.company.name }}<template v-if="item.contratoCodigo"> — {{ item.contratoCodigo }}</template>
        </span>
        <span v-else class="grey--text">B2C</span>
      </template>

      <template v-slot:item.actions="{ item }">
        <v-btn
          small text color="success"
          :disabled="!item.userId"
          :href="profileUrl(item)"
          target="_blank"
          title="Ir al perfil para renovar"
        >
          Renovar
          <v-icon right small>mdi-open-in-new</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="4000">
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { utilsMixin } from '../mixins/utilsMixin';
import MembershipsService from '../services/memberships.service';

// Sentinel para el ítem "sin línea de negocio" del filtro Tipo — distinto de
// cualquier valor real de lineaNegocio (texto libre).
const EMPTY_TYPE_VALUE = '__EMPTY__';

// Mismos colores que las ventanas de Renovaciones B2B, para que la urgencia
// se lea igual en ambas vistas.
const WINDOW_META = {
  expired: { label: 'Vencidas', caption: 'Contactar hoy', color: '#C62828' },
  due7: { label: '≤ 7 días', caption: 'Vencen esta semana', color: '#E65100' },
  due15: { label: '≤ 15 días', caption: 'Próximas dos semanas', color: '#F9A825' },
  due40: { label: '≤ 40 días', caption: 'Resto de la ventana', color: '#2E7D32' },
};

export default {
  name: 'Vencimientos',

  mixins: [utilsMixin],

  data() {
    return {
      loadingData: false,
      items: [],
      search: '',
      // Evita que un recargado manual (botón "Recargar") pise la selección
      // de filtros que el usuario ya haya ajustado — solo se preseleccionan
      // "todos" la primera vez que llegan datos.
      filtersInitialized: false,
      // Filtros "en edición" (ligados a los controles) — no afectan la tabla
      // hasta que se presiona "Aplicar filtro". Sí afectan en vivo las
      // opciones disponibles de los demás filtros (cascada), porque eso es
      // barato: no recalcula la tabla.
      typeFilterPending: [],
      companyFilterPending: [],
      // false (default) = excluye del listado los contratos churned.
      churnFilterPending: false,
      // Filtros aplicados — únicos que usan la tabla y las tarjetas.
      typeFilterApplied: [],
      companyFilterApplied: [],
      churnFilterApplied: false,
      // '' = todas las ventanas; si no, una clave de WINDOW_META. Se aplica
      // al instante, igual que las tarjetas de Renovaciones B2B.
      windowFilter: '',
      headers: [
        { text: 'Tipo', value: 'lineaNegocio', sortable: true, align: 'start' },
        { text: 'Vencimiento', value: 'expirationDate', sortable: true, align: 'start' },
        { text: 'Días', value: 'daysUntilExpiry', sortable: true, align: 'start' },
        { text: 'Mascota', value: 'petName', sortable: true, align: 'start' },
        { text: 'Usuario', value: 'userName', sortable: true, align: 'start' },
        // Oculta: el email va bajo el nombre, pero se mantiene la columna
        // para que la búsqueda del v-data-table también lo encuentre.
        { text: 'Email', value: 'email', align: ' d-none' },
        { text: 'Plan', value: 'planName', sortable: true, align: 'start' },
        { text: 'Empresa / Contrato', value: 'contratoCodigo', sortable: true, align: 'start' },
        { text: '', value: 'actions', sortable: false, align: 'end' },
      ],
      // Snackbar (utilsMixin)
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'red',
    };
  },

  computed: {
    // Opciones del filtro "Tipo": valores reales de lineaNegocio presentes en
    // los datos cargados, no una lista fija — el campo es texto libre. Se
    // agrega un ítem "-" para las membresías sin lineaNegocio.
    typeFilterItems() {
      const values = [...new Set(this.items.map((i) => i.lineaNegocio).filter(Boolean))];
      const items = values.sort().map((v) => ({
        text: v,
        value: v,
        count: this.items.filter((i) => i.lineaNegocio === v).length,
      }));
      const emptyCount = this.items.filter((i) => !i.lineaNegocio).length;
      if (emptyCount) {
        items.push({ text: '-', value: EMPTY_TYPE_VALUE, count: emptyCount });
      }
      return items;
    },

    // Opciones del filtro "Empresa / Contrato": combinaciones presentes en los
    // datos que además calzan con el Tipo seleccionado (en edición) — cascada
    // en vivo, sin esperar al botón "Aplicar filtro".
    companyFilterItems() {
      const scoped = this.items.filter((i) => this.matchesType(i, this.typeFilterPending));
      const values = [...new Set(scoped.map((i) => this.companyKey(i)))];
      return values.sort().map((v) => ({ text: v, value: v }));
    },

    // true si están todas las opciones disponibles seleccionadas — el select
    // muestra un resumen en vez de un chip por cada empresa/contrato.
    allCompaniesSelected() {
      return (
        this.companyFilterItems.length > 0
        && this.companyFilterPending.length === this.companyFilterItems.length
      );
    },

    // Habilita el botón "Aplicar filtro" solo si hay cambios reales respecto
    // de lo ya aplicado — evita recalcular la tabla sin necesidad.
    hasPendingChanges() {
      return (
        this.arraysDiffer(this.typeFilterPending, this.typeFilterApplied)
        || this.arraysDiffer(this.companyFilterPending, this.companyFilterApplied)
        || this.churnFilterPending !== this.churnFilterApplied
      );
    },

    // Todo menos la ventana: base para los conteos de las tarjetas.
    scopedItems() {
      return this.items.filter((i) => {
        if (!this.matchesType(i, this.typeFilterApplied)) return false;
        if (this.companyFilterApplied.length && !this.companyFilterApplied.includes(this.companyKey(i))) {
          return false;
        }
        // Apagado (default) = los contratos churned no aparecen en el listado.
        if (!this.churnFilterApplied && i.isChurned === true) return false;
        return true;
      });
    },

    filteredItems() {
      if (!this.windowFilter) return this.scopedItems;
      return this.scopedItems.filter((i) => this.windowKey(i) === this.windowFilter);
    },

    summaryWindows() {
      return Object.keys(WINDOW_META).map((key) => ({
        key,
        ...WINDOW_META[key],
        count: this.scopedItems.filter((i) => this.windowKey(i) === key).length,
      }));
    },

    hasActiveFilters() {
      return (
        Boolean(this.windowFilter)
        || this.churnFilterPending
        || this.churnFilterApplied
        || this.typeFilterPending.length !== this.typeFilterItems.length
        || this.typeFilterApplied.length !== this.typeFilterItems.length
        || this.companyFilterPending.length !== this.companyFilterItems.length
        || this.companyFilterApplied.length !== this.companyFilterItems.length
      );
    },

    emptyText() {
      if (this.loadingData) return 'Cargando...';
      return this.windowFilter
        ? 'No hay membresías en esta ventana con los filtros actuales'
        : 'No hay membresías por vencer en los próximos 40 días';
    },
  },

  watch: {
    // Si el Tipo seleccionado deja fuera empresas/contratos ya elegidos,
    // se limpian para no dejar seleccionada una opción que ya no existe.
    typeFilterPending() {
      const validKeys = new Set(this.companyFilterItems.map((o) => o.value));
      this.companyFilterPending = this.companyFilterPending.filter((v) => validKeys.has(v));
    },
  },

  async created() {
    await this.loadData();
  },

  methods: {
    // true si `item` calza con alguno de los Tipos seleccionados (sentinela
    // EMPTY_TYPE_VALUE = sin lineaNegocio). Sin selección, calza todo.
    matchesType(item, selected) {
      if (!selected.length) return true;
      return selected.some((v) => (v === EMPTY_TYPE_VALUE ? !item.lineaNegocio : item.lineaNegocio === v));
    },

    arraysDiffer(a, b) {
      if (a.length !== b.length) return true;
      const bSet = new Set(b);
      return a.some((v) => !bSet.has(v));
    },

    // Copia los filtros "en edición" a los "aplicados" — único punto donde
    // la tabla se recalcula por filtros.
    applyFilters() {
      this.typeFilterApplied = [...this.typeFilterPending];
      this.companyFilterApplied = [...this.companyFilterPending];
      this.churnFilterApplied = this.churnFilterPending;
    },

    async loadData() {
      this.loadingData = true;
      const res = await MembershipsService.getExpiring();
      if (res.status === 'ok') {
        this.items = res.data || [];
        if (!this.filtersInitialized) {
          this.selectAllFilters();
          this.applyFilters();
          this.filtersInitialized = true;
        }
      } else {
        this.showErrorSnackbar('Error al cargar las membresías por vencer');
      }
      this.loadingData = false;
    },

    // Por defecto todos los valores de Tipo y Empresa/Contrato quedan
    // seleccionados (equivalente a "sin filtro", pero mostrando explícito
    // en los chips/select qué se está incluyendo).
    selectAllFilters() {
      this.typeFilterPending = this.typeFilterItems.map((o) => o.value);
      this.companyFilterPending = this.companyFilterItems.map((o) => o.value);
    },

    // Limpiar es una acción explícita, así que se aplica de inmediato sin
    // pasar por "Aplicar filtro".
    clearFilters() {
      this.windowFilter = '';
      this.churnFilterPending = false;
      this.selectAllFilters();
      this.applyFilters();
    },

    toggleWindow(key) {
      this.windowFilter = this.windowFilter === key ? '' : key;
    },

    // Clave de agrupación para el filtro y las opciones "Empresa / Contrato" —
    // misma regla que se usa para renderizar la columna en la tabla.
    companyKey(item) {
      if (item.company && item.company.name) {
        return item.contratoCodigo ? `${item.company.name} — ${item.contratoCodigo}` : item.company.name;
      }
      return 'B2C';
    },

    // Link directo a la 360 del usuario con el modal de renovación de esta membresía abierto.
    profileUrl(item) {
      if (!item.userId) return null;
      return this.$router.resolve({
        name: 'Vision360',
        params: { pgId: item.pgId },
        query: { renew: item.id },
      }).href;
    },

    hasDays(item) {
      return item.daysUntilExpiry !== null && item.daysUntilExpiry !== undefined;
    },

    windowKey(item) {
      if (!this.hasDays(item)) return null;
      const d = item.daysUntilExpiry;
      if (d < 0) return 'expired';
      if (d <= 7) return 'due7';
      if (d <= 15) return 'due15';
      return 'due40';
    },

    windowColor(key) {
      return (WINDOW_META[key] || {}).color || '#8593A3';
    },

    daysLabel(item) {
      const d = item.daysUntilExpiry;
      if (d < 0) return `vencida hace ${Math.abs(d)} d`;
      if (d === 0) return 'vence hoy';
      return `${d} d`;
    },

    // lineaNegocio es texto libre (sin enum) — el color se deriva por hash en
    // vez de un mapa fijo, así cualquier valor nuevo recibe un color estable.
    typeColor(item) {
      if (!item.lineaNegocio) return 'grey';
      return this.hashColor(item.lineaNegocio);
    },

    // Color del chip de Tipo en el filtro — mismo esquema que typeColor, pero
    // a partir del valor de la opción (puede ser el sentinela "-").
    typeChipColor(value) {
      if (value === EMPTY_TYPE_VALUE) return 'grey';
      return this.hashColor(value);
    },

    hashColor(text) {
      const palette = ['blue', 'indigo', 'teal', 'brown', 'deep-purple', 'pink', 'cyan', 'green', 'orange', 'blue-grey'];
      let hash = 0;
      for (let i = 0; i < text.length; i += 1) hash = (hash * 31 + text.charCodeAt(i)) | 0;
      return palette[Math.abs(hash) % palette.length];
    },
  },
};
</script>

<style scoped>
.page-title {
  color: #104b94;
  font-weight: 700;
}
.page-desc {
  font-size: 13px;
  color: #555;
  max-width: 720px;
}
.window-card {
  border-radius: 8px;
  cursor: pointer;
  height: 100%;
}
.window-card--active {
  border-width: 2px;
}
.window-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
  font-family: Inter, sans-serif;
}
.window-count {
  font-size: 22px;
  font-weight: 700;
  color: #1B1D21;
  font-family: Inter, sans-serif;
}
.window-caption {
  font-size: 11px;
  color: #8593A3;
  font-family: Inter, sans-serif;
}
.row-sub {
  font-size: 11px;
  color: #8593A3;
}

/* Evita que la flecha de orden parta el título en dos líneas: cada encabezado
   queda en una sola línea, manteniendo la altura y la simetría del header. */
::v-deep .v-data-table-header th {
  white-space: nowrap;
}
</style>
